import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { ContextType } from '@lit/context';
import { Task } from '@lit/task';
import { SlSelect } from '@shoelace-style/shoelace';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { live } from 'lit/directives/live.js';
import { api } from '../api.js';
import { consume, contexts, EventTask } from '../contexts.js';
import { markdown } from '../markdown.js';
import { CHECK } from '@flakiness/shared/common/utils.js';

export type LocalCheckout = {
  gitFolder: string,
  defaultBranch: string,
}

@customElement('pick-local-checkout')
class PickLocalCheckout extends LitElement {
  @consume({ context: contexts.serverInfo, subscribe: true }) private _serverInfo?: ContextType<typeof contexts.serverInfo>;

  private _localCheckoutsTask = new Task(this, {
    args: () => [] as const,
    task: async ([], { signal }) => {
      return await api.projectSources.getLocalCheckouts.GET();
    }
  });

  private _branchesTask = new Task(this, {
    args: () => [this._checkoutFolder] as const,
    task: async ([gitFolder], { signal }) => {
      CHECK(gitFolder);
      return await api.projectSources.getLocalBranches.GET({
        gitFolder,
      });
    }
  });

  private _defaultBranchTask = new Task(this, {
    args: () => [this._checkoutFolder, this._branchesTask.value] as const,
    task: async ([gitFolder, branches], { signal }) => {
      CHECK(gitFolder && branches && branches.length); 
      const mainBranch = branches.find(branch => branch === 'main' || branch.split('/').pop() === 'main');
      const masterBranch = branches.find(branch => branch === 'master' || branch.split('/').pop() === 'master');
      return mainBranch ?? masterBranch ?? branches[0];
    }
  })

  private _localCheckoutTask = new EventTask(this, 'local-checkout', {
    args: () => [this._checkoutFolder, this._defaultBranchTask.value, this._defaultBranchOverride] as const,
    task: async ([gitFolder, defaultBranch, branchOverride], { signal }) => {
      CHECK(gitFolder && defaultBranch);
      const result: LocalCheckout = {
        gitFolder,
        defaultBranch: branchOverride ?? defaultBranch,
      }
      return result;
    }
  })

  @query('#checkout-path-selector') _hCheckoutPath?: SlSelect;
  @query('#default-branch-selector') _hDefaultBranch?: SlSelect;
  @state() private _checkoutFolder?: string;
  @state() private _defaultBranchOverride?: string;

  override render() {
    const defaultBranch = this._defaultBranchTask.value;

    return html`
      <v-box style="align-items: start;">
        ${this._serverInfo ? helpText(this._serverInfo) : nothing}

        <h-box>
          <sl-select placeholder="Git Checkout" id=checkout-path-selector hoist @sl-input=${() => {
            this._checkoutFolder = this._hCheckoutPath?.value as (string|undefined);
            this._defaultBranchOverride = undefined;
          }}>
            ${this._localCheckoutsTask.render({
              complete: folders => folders.map(folder => html`<sl-option value=${folder}>${folder}</sl-option>`),
            })}
          </sl-select>

          ${this._branchesTask.render({
            pending: () => html`<sl-spinner></sl-spinner>`,
            complete: branches => html`
              <sl-select id=default-branch-selector placeholder="Default Branch" hoist value=${live(defaultBranch)}
                @sl-input=${() => {
                  this._defaultBranchOverride = this._hDefaultBranch?.value as (string|undefined);
                }}
              >
                <sl-icon slot=prefix library=boxicons name=bx-git-branch></sl-icon>
                ${branches.map(branch => html`<sl-option value=${branch}>${branch}</sl-option>`)}
              </sl-select>
            `,
          })}
        </h-box>
        
      </v-box>
    `;
  }

  static styles = [css`
  `];
}

const helpText = (serverInfo: WireTypes.ServerInfo) => markdown`
Server is configured to show git repositories located in **${serverInfo.fsGitManagerRoot}**
`